"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const MAX_WIDTH = 1000;
// Rendered PDF pages are stacked into one canvas, so the width has to leave the
// tallest allowed stack inside the ~16M pixel canvas ceiling mobile Safari has.
const PDF_WIDTH = 1200;
const MAX_PDF_PAGES = 4;
const PDF_PAGE_GAP = 16;

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

async function renderImage(file: File): Promise<HTMLCanvasElement> {
  // Phone cameras record portrait shots as landscape plus an EXIF rotation, so
  // without this the letter arrives on its side.
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  const scale = Math.min(1, MAX_WIDTH / bitmap.width);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return canvas;
}

/**
 * Flattens a PDF into a single tall canvas so the redactor treats it exactly
 * like a photo. pdf.js normally loads its worker from a URL no bundler here
 * resolves cleanly; handing it the worker module on `globalThis` makes it run
 * on the main thread instead, which is fine for a few pages of a letter.
 */
async function renderPdf(file: File): Promise<{ canvas: HTMLCanvasElement; pages: number }> {
  const [pdfjs, worker] = await Promise.all([
    import("pdfjs-dist"),
    import("pdfjs-dist/build/pdf.worker.min.mjs"),
  ]);
  (globalThis as { pdfjsWorker?: unknown }).pdfjsWorker = worker;

  const doc = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  const rendered: HTMLCanvasElement[] = [];
  try {
    for (let n = 1; n <= Math.min(doc.numPages, MAX_PDF_PAGES); n++) {
      const page = await doc.getPage(n);
      const viewport = page.getViewport({
        scale: PDF_WIDTH / page.getViewport({ scale: 1 }).width,
      });
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(viewport.width);
      canvas.height = Math.round(viewport.height);
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("no 2d context");
      await page.render({ canvas, canvasContext: ctx, viewport }).promise;
      rendered.push(canvas);
    }
  } finally {
    await doc.destroy();
  }

  const canvas = document.createElement("canvas");
  canvas.width = PDF_WIDTH;
  canvas.height =
    rendered.reduce((h, p) => h + p.height, 0) + PDF_PAGE_GAP * (rendered.length - 1);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no 2d context");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  let y = 0;
  for (const page of rendered) {
    ctx.drawImage(page, 0, y);
    y += page.height + PDF_PAGE_GAP;
  }
  return { canvas, pages: doc.numPages };
}

/**
 * Lets the applicant black out anything on their evidence letter before it ever
 * leaves the device: the boxes are burned into the pixels we upload, so what we
 * store is the redacted image, not an annotated original.
 */
export function ProofRedactor({
  onChange,
}: {
  onChange: (file: Blob | null) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imageRef = useRef<HTMLCanvasElement | null>(null);
  const [rects, setRects] = useState<Rect[]>([]);
  const [drag, setDrag] = useState<Rect | null>(null);
  const [hasImage, setHasImage] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Drawing needs `touch-action: none`, which on a phone means a swipe over the
  // image draws instead of scrolling the page. So touch devices start in
  // scroll mode and opt into drawing; a mouse just draws.
  const [drawing, setDrawing] = useState(false);

  useEffect(() => {
    setDrawing(window.matchMedia("(pointer: fine)").matches);
  }, []);

  const draw = useCallback((pending: Rect | null, boxes: Rect[]) => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#000";
    for (const r of boxes) ctx.fillRect(r.x, r.y, r.w, r.h);
    if (pending) {
      ctx.fillStyle = "rgba(0,0,0,0.65)";
      ctx.fillRect(pending.x, pending.y, pending.w, pending.h);
    }
  }, []);

  const emit = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      onChange(blob);
      // Phones refuse to export a canvas past their memory ceiling, and a null
      // blob would otherwise just leave the submit button dead with no reason.
      if (!blob) {
        setError("This device couldn't handle a file that big. Try a photo of the letter instead.");
      }
    }, "image/jpeg", 0.85);
  }, [onChange]);

  useEffect(() => {
    if (!hasImage) return;
    draw(drag, rects);
    if (!drag) emit();
  }, [hasImage, rects, drag, draw, emit]);

  const onPickFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setNote(null);
    setLoading(true);
    try {
      const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
      let source: HTMLCanvasElement;
      if (isPdf) {
        const { canvas, pages } = await renderPdf(file);
        source = canvas;
        if (pages > MAX_PDF_PAGES) {
          setNote(
            `That PDF has ${pages} pages, so we've taken the first ${MAX_PDF_PAGES}. Upload a shorter extract if your proof is further in.`,
          );
        }
      } else {
        source = await renderImage(file);
      }
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = source.width;
      canvas.height = source.height;
      imageRef.current = source;
      setRects([]);
      setDrag(null);
      setHasImage(true);
      setFileName(file.name);
    } catch {
      setError("We couldn't open that file. Try a photo, a screenshot or a PDF.");
      onChange(null);
    } finally {
      setLoading(false);
    }
  };

  const toCanvasPoint = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const box = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - box.left) / box.width) * canvas.width,
      y: ((e.clientY - box.top) / box.height) * canvas.height,
    };
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!hasImage || !drawing) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = toCanvasPoint(e);
    setDrag({ x: p.x, y: p.y, w: 0, h: 0 });
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drag) return;
    const p = toCanvasPoint(e);
    setDrag({ ...drag, w: p.x - drag.x, h: p.y - drag.y });
  };

  const onPointerUp = () => {
    if (!drag) return;
    const normalised: Rect = {
      x: Math.min(drag.x, drag.x + drag.w),
      y: Math.min(drag.y, drag.y + drag.h),
      w: Math.abs(drag.w),
      h: Math.abs(drag.h),
    };
    setDrag(null);
    if (normalised.w > 4 && normalised.h > 4) setRects((prev) => [...prev, normalised]);
  };

  return (
    <div className="space-y-4">
      <input
        id="proof-file"
        type="file"
        accept="image/*,application/pdf"
        onChange={(e) => onPickFile(e.target.files?.[0])}
        className="sr-only"
      />

      <label
        htmlFor="proof-file"
        className="flex cursor-pointer items-center gap-4 rounded-2xl border border-dashed border-border px-5 py-6 transition-colors hover:border-foreground/25"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 font-polysans text-lg text-primary">
          +
        </span>
        <span className="min-w-0">
          <span className="block truncate font-polysans text-sm font-medium">
            {loading ? "Opening it…" : (fileName ?? "Add a photo, screenshot or PDF")}
          </span>
          <span className="mt-0.5 block font-polysans text-xs font-light text-muted-foreground">
            You can black out anything private before it&apos;s sent. We only ever receive the
            blacked-out copy.
          </span>
        </span>
      </label>

      <div className={hasImage ? "space-y-3" : "hidden"}>
        <p className="font-polysans text-xs font-light text-muted-foreground">
          {drawing
            ? "Drag across anything you'd rather we didn't see, like your address or your National Insurance number."
            : "Check it over, then tap Black something out to cover your address, your National Insurance number, or anything else private."}
        </p>
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          className={cn(
            "w-full rounded-2xl border bg-muted",
            drawing ? "cursor-crosshair touch-none border-primary" : "border-border",
          )}
        />
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant={drawing ? "default" : "outline"}
            size="sm"
            className="rounded-full font-polysans"
            onClick={() => setDrawing((on) => !on)}
          >
            {drawing ? "Done blacking out" : "Black something out"}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full font-polysans"
            onClick={() => setRects((prev) => prev.slice(0, -1))}
            disabled={rects.length === 0}
          >
            Undo box
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full font-polysans"
            onClick={() => setRects([])}
            disabled={rects.length === 0}
          >
            Clear boxes
          </Button>
          <span className="self-center font-polysans text-xs font-light text-muted-foreground">
            {rects.length} box{rects.length === 1 ? "" : "es"}
          </span>
        </div>
      </div>

      {note ? (
        <p className="font-polysans text-xs font-light text-muted-foreground">{note}</p>
      ) : null}

      {error ? (
        <p className="font-polysans text-sm text-destructive">{error}</p>
      ) : null}
    </div>
  );
}
